import React, { useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import {
  Box,
  Paper,
  Typography,
  Button,
  CircularProgress,
  Alert,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Chip,
  IconButton,
  Stack,
  Switch,
  FormControlLabel,
} from '@mui/material';
import {
  Add as AddIcon,
  Edit as EditIcon,
  Delete as DeleteIcon,
  Lock as LockIcon,
  Refresh as RefreshIcon,
} from '@mui/icons-material';
import api from '../api';
import { useAuth } from '../AuthContext';

function AdminUserManagement() {
  const { user } = useAuth();
  const queryClient = useQueryClient();
  
  // State for dialogs
  const [createDialogOpen, setCreateDialogOpen] = useState(false);
  const [editDialogOpen, setEditDialogOpen] = useState(false);
  const [passwordDialogOpen, setPasswordDialogOpen] = useState(false);
  const [selectedUser, setSelectedUser] = useState(null);
  
  // State for forms
  const [createForm, setCreateForm] = useState({
    email: '',
    username: '',
    password: '',
    role: 'analytics'
  });
  const [editForm, setEditForm] = useState({
    email: '',
    username: '',
    role: '',
    isActive: true
  });
  const [passwordForm, setPasswordForm] = useState({
    currentPassword: '',
    newPassword: '',
    confirmPassword: ''
  });
  
  const [alerts, setAlerts] = useState([]);
  
  const showAlert = (message, severity = 'info') => {
    const alert = { id: Date.now(), message, severity };
    setAlerts(prev => [...prev, alert]);
    setTimeout(() => {
      setAlerts(prev => prev.filter(a => a.id !== alert.id));
    }, 5000);
  };


  
  // Fetch admin users
  const { data: adminUsers = [], isLoading, error, refetch } = useQuery({
    queryKey: ['adminUsers'],
    queryFn: async () => {
      const response = await api.get('/admin-users');
      return response.data;
    },
    enabled: user?.role === 'admin' || user?.role === 'super_admin'
  });
  
  // Create user mutation
  const createUserMutation = useMutation({
    mutationFn: async (userData) => {
      const response = await api.post('/admin-users', userData);
      return response.data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries('adminUsers');
      setCreateDialogOpen(false);
      setCreateForm({ email: '', username: '', password: '', role: 'analytics' });
      showAlert('Admin user created successfully', 'success');
    },
    onError: (error) => {
      showAlert(error.response?.data?.error || 'Failed to create user', 'error');
    }
  });
  
  // Update user mutation
  const updateUserMutation = useMutation({
    mutationFn: async ({ userId, userData }) => {
      const response = await api.put(`/admin-users/${userId}`, userData);
      return response.data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries('adminUsers');
      setEditDialogOpen(false);
      showAlert('Admin user updated successfully', 'success');
    },
    onError: (error) => {
      showAlert(error.response?.data?.error || 'Failed to update user', 'error');
    }
  });
  
  // Update password mutation
  const updatePasswordMutation = useMutation({
    mutationFn: async ({ userId, passwordData }) => {
      const response = await api.put(`/admin-users/${userId}/changecred`, passwordData);
      return response.data;
    },
    onSuccess: () => {
      setPasswordDialogOpen(false);
      setPasswordForm({ currentPassword: '', newPassword: '', confirmPassword: '' });
      showAlert('Password updated successfully', 'success');
    },
    onError: (error) => {
      showAlert(error.response?.data?.error || 'Failed to update password', 'error');
    }
  });
  
  // Delete user mutation
  const deleteUserMutation = useMutation({
    mutationFn: async (userId) => {
      const response = await api.delete(`/admin-users/${userId}`);
      return response.data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries('adminUsers');
      showAlert('Admin user deactivated successfully', 'success');
    },
    onError: (error) => {
      showAlert(error.response?.data?.error || 'Failed to deactivate user', 'error');
    }
  });
  
  const handleCreateUser = () => {
    if (!createForm.email || !createForm.username || !createForm.password) {
      showAlert('Please fill in all required fields', 'error');
      return;
    }
    createUserMutation.mutate(createForm);
  };
  
  const handleEditUser = (userToEdit) => {
    setSelectedUser(userToEdit);
    setEditForm({
      email: userToEdit.email,
      username: userToEdit.username,
      role: userToEdit.role,
      isActive: userToEdit.isActive
    });
    setEditDialogOpen(true);
  };
  
  const handleUpdateUser = () => {
    updateUserMutation.mutate({
      userId: selectedUser.id,
      userData: editForm
    });
  };
  
  const handleChangePassword = (userToEdit) => {
    setSelectedUser(userToEdit);
    setPasswordForm({ currentPassword: '', newPassword: '', confirmPassword: '' });
    setPasswordDialogOpen(true);
  };
  
  const handleUpdatePassword = () => {
    if (!passwordForm.newPassword) {
      showAlert('New password is required', 'error');
      return;
    }
    
    if (passwordForm.newPassword !== passwordForm.confirmPassword) {
      showAlert('Passwords do not match', 'error');
      return;
    }
    
    const isOwnPassword = selectedUser.id === user.id;
    
    updatePasswordMutation.mutate({
      userId: selectedUser.id,
      passwordData: {
        currentPassword: isOwnPassword ? passwordForm.currentPassword : undefined,
        newPassword: passwordForm.newPassword
      }
    });
  };
  
  const handleDeleteUser = (userId) => {
    if (window.confirm('Are you sure you want to deactivate this user?')) {
      deleteUserMutation.mutate(userId);
    }
  };
  
  const getRoleColor = (role) => {
    switch (role) {
      case 'super_admin': return 'error';
      case 'admin': return 'warning';
      case 'manager': return 'info';
      case 'analytics': return 'primary';
      default: return 'default';
    }
  };

  const getRoleLabel = (role) => {
    switch (role) {
      case 'super_admin': return 'Super Admin';
      case 'admin': return 'Admin';
      case 'manager': return 'Manager';
      case 'analytics': return 'Analytics';
      default: return role;
    }
  };
  
  if (!user || (user.role !== 'admin' && user.role !== 'super_admin')) {
    return (
      <Box display="flex" justifyContent="center" mt={4}>
        <Typography variant="h6">Access denied. Admin privileges required.</Typography>
      </Box>
    );
  }
  
  return (
    <Box>
      <Stack direction="row" spacing={2} sx={{ mb: 3 }}>
        <Typography variant="h4" component="div" flexGrow={1}>
          Admin User Management
        </Typography>
        <Button
          variant="outlined"
          startIcon={<RefreshIcon />}
          onClick={() => refetch()}
          disabled={isLoading}
        >
          Refresh
        </Button>
        {user.role === 'super_admin' && (
          <Button
            variant="contained"
            startIcon={<AddIcon />}
            onClick={() => setCreateDialogOpen(true)}
          >
            Create User
          </Button>
        )}
      </Stack>
      
      {alerts.map(alert => (
        <Alert key={alert.id} severity={alert.severity} sx={{ mb: 2 }}>
          {alert.message}
        </Alert>
      ))}
      
      {error && (
        <Alert severity="error" sx={{ mb: 2 }}>
          Failed to load admin users: {error.message}
        </Alert>
      )}
      
      <Paper sx={{ p: 3 }}>
        {isLoading ? (
          <Box display="flex" justifyContent="center" py={4}>
            <CircularProgress />
          </Box>
        ) : (
          <TableContainer>
            <Table>
              <TableHead>
                <TableRow>
                  <TableCell>Username</TableCell>
                  <TableCell>Email</TableCell>
                  <TableCell>Role</TableCell>
                  <TableCell>Status</TableCell>
                  <TableCell>Created</TableCell>
                  <TableCell>Last Login</TableCell>
                  <TableCell>Actions</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {adminUsers.map((adminUser) => (
                  <TableRow key={adminUser.id}>
                    <TableCell>{adminUser.username}</TableCell>
                    <TableCell>{adminUser.email}</TableCell>
                    <TableCell>
                      <Chip 
                        label={getRoleLabel(adminUser.role)} 
                        color={getRoleColor(adminUser.role)}
                        size="small"
                      />
                    </TableCell>
                    <TableCell>
                      <Chip 
                        label={adminUser.isActive ? 'Active' : 'Inactive'} 
                        color={adminUser.isActive ? 'success' : 'default'}
                        size="small"
                      />
                    </TableCell>
                    <TableCell>
                      {new Date(adminUser.createdAt).toLocaleDateString()}
                    </TableCell>
                    <TableCell>
                      {adminUser.lastLogin ? new Date(adminUser.lastLogin).toLocaleDateString() : 'Never'}
                    </TableCell>
                    <TableCell>
                      <Stack direction="row" spacing={1}>
                        <IconButton
                          size="small"
                          onClick={() => handleChangePassword(adminUser)}
                          title="Change Password"
                        >
                          <LockIcon />
                        </IconButton>
                        {user.role === 'super_admin' && (
                          <>
                            <IconButton
                              size="small"
                              onClick={() => handleEditUser(adminUser)}
                              title="Edit User"
                            >
                              <EditIcon />
                            </IconButton>
                            {adminUser.id !== user.id && (
                              <IconButton
                                size="small"
                                onClick={() => handleDeleteUser(adminUser.id)}
                                title="Deactivate User"
                              >
                                <DeleteIcon />
                              </IconButton>
                            )}
                          </>
                        )}
                      </Stack>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
        )}
      </Paper>
      
      {/* Create User Dialog */}
      <Dialog open={createDialogOpen} onClose={() => setCreateDialogOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle>Create Admin User</DialogTitle>
        <DialogContent>
          <Box sx={{ pt: 2 }}>
            <TextField
              fullWidth
              label="Email"
              type="email"
              value={createForm.email}
              onChange={(e) => setCreateForm(prev => ({ ...prev, email: e.target.value }))}
              sx={{ mb: 2 }}
            />
            <TextField
              fullWidth
              label="Username"
              value={createForm.username}
              onChange={(e) => setCreateForm(prev => ({ ...prev, username: e.target.value }))}
              sx={{ mb: 2 }}
            />
            <TextField
              fullWidth
              label="Password"
              type="password"
              value={createForm.password}
              onChange={(e) => setCreateForm(prev => ({ ...prev, password: e.target.value }))}
              sx={{ mb: 2 }}
            />
            <FormControl fullWidth sx={{ mb: 2 }}>
              <InputLabel>Role</InputLabel>
              <Select
                value={createForm.role}
                label="Role"
                onChange={(e) => setCreateForm(prev => ({ ...prev, role: e.target.value }))}
              >
                <MenuItem value="analytics">Analytics</MenuItem>
                <MenuItem value="manager">Manager</MenuItem>
                <MenuItem value="admin">Admin</MenuItem>
                <MenuItem value="super_admin">Super Admin</MenuItem>
              </Select>
            </FormControl>
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setCreateDialogOpen(false)}>Cancel</Button>
          <Button 
            onClick={handleCreateUser} 
            variant="contained"
            disabled={createUserMutation.isLoading}
          >
            {createUserMutation.isLoading ? <CircularProgress size={20} /> : 'Create User'}
          </Button>
        </DialogActions>
      </Dialog>
      
      {/* Edit User Dialog */}
      <Dialog open={editDialogOpen} onClose={() => setEditDialogOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle>Edit Admin User</DialogTitle>
        <DialogContent>
          <Box sx={{ pt: 2 }}>
            <TextField
              fullWidth
              label="Email"
              type="email"
              value={editForm.email}
              onChange={(e) => setEditForm(prev => ({ ...prev, email: e.target.value }))}
              sx={{ mb: 2 }}
            />
            <TextField
              fullWidth
              label="Username"
              value={editForm.username}
              onChange={(e) => setEditForm(prev => ({ ...prev, username: e.target.value }))}
              sx={{ mb: 2 }}
            />
            <FormControl fullWidth sx={{ mb: 2 }}>
              <InputLabel>Role</InputLabel>
              <Select
                value={editForm.role}
                label="Role"
                onChange={(e) => setEditForm(prev => ({ ...prev, role: e.target.value }))}
              >
                <MenuItem value="analytics">Analytics</MenuItem>
                <MenuItem value="manager">Manager</MenuItem>
                <MenuItem value="admin">Admin</MenuItem>
                <MenuItem value="super_admin">Super Admin</MenuItem>
              </Select>
            </FormControl>
            <FormControlLabel
              control={
                <Switch
                  checked={editForm.isActive}
                  onChange={(e) => setEditForm(prev => ({ ...prev, isActive: e.target.checked }))}
                />
              }
              label="Active"
            />
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setEditDialogOpen(false)}>Cancel</Button>
          <Button 
            onClick={handleUpdateUser} 
            variant="contained"
            disabled={updateUserMutation.isLoading}
          >
            {updateUserMutation.isLoading ? <CircularProgress size={20} /> : 'Update User'}
          </Button>
        </DialogActions>
      </Dialog>
      
      {/* Change Password Dialog */}
      <Dialog open={passwordDialogOpen} onClose={() => setPasswordDialogOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle>
          Change Password - {selectedUser?.username}
        </DialogTitle>
        <DialogContent>
          <Box sx={{ pt: 2 }}>
            {selectedUser?.id === user.id && (
              <TextField
                fullWidth
                label="Current Password"
                type="password"
                value={passwordForm.currentPassword}
                onChange={(e) => setPasswordForm(prev => ({ ...prev, currentPassword: e.target.value }))}
                sx={{ mb: 2 }}
              />
            )}
            <TextField
              fullWidth
              label="New Password"
              type="password"
              value={passwordForm.newPassword}
              onChange={(e) => setPasswordForm(prev => ({ ...prev, newPassword: e.target.value }))}
              sx={{ mb: 2 }}
            />
            <TextField
              fullWidth
              label="Confirm New Password"
              type="password"
              value={passwordForm.confirmPassword}
              onChange={(e) => setPasswordForm(prev => ({ ...prev, confirmPassword: e.target.value }))}
              sx={{ mb: 2 }}
            />
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setPasswordDialogOpen(false)}>Cancel</Button>
          <Button 
            onClick={handleUpdatePassword} 
            variant="contained"
            disabled={updatePasswordMutation.isLoading}
          >
            {updatePasswordMutation.isLoading ? <CircularProgress size={20} /> : 'Update Password'}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}

export default AdminUserManagement; 